import React, { useState, useEffect, useRef, useCallback } from 'react';
import API from '../lib/api';
import { ClipboardList, Plus, Camera, Calendar, Clock, PenTool, Send, Loader2, Image, Trash2, CheckCircle, XCircle, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import SignaturePad from '../components/SignaturePad';

const STATUS = {
  pending: { label: 'Pendiente', bg: 'bg-amber-50 text-amber-700' },
  approved: { label: 'Aprobado', bg: 'bg-green-50 text-green-700' },
  rejected: { label: 'Rechazado', bg: 'bg-red-50 text-red-700' },
};

export default function WorkReportsWorkerPage() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [signatureStep, setSignatureStep] = useState(false);
  const [detailReport, setDetailReport] = useState(null);
  const [photos, setPhotos] = useState([]);
  const photoInputRef = useRef(null);
  const [form, setForm] = useState({
    description: '',
    date: new Date().toISOString().split('T')[0],
    start_time: '',
    end_time: ''
  });

  const fetchReports = useCallback(async () => {
    try {
      const res = await API.get('/work-reports');
      setReports(Array.isArray(res.data) ? res.data : []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const handlePhotos = (e) => {
    const files = Array.from(e.target.files);
    setPhotos(prev => [...prev, ...files].slice(0, 5));
    e.target.value = '';
  };

  const handleSignatureComplete = async (signatureData) => {
    setSignatureStep(false);
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('description', form.description);
      fd.append('date', form.date);
      fd.append('start_time', form.start_time);
      fd.append('end_time', form.end_time);
      fd.append('signature', signatureData);
      photos.forEach(p => fd.append('photos', p));

      await API.post('/work-reports', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setDialogOpen(false);
      setForm({ description: '', date: new Date().toISOString().split('T')[0], start_time: '', end_time: '' });
      setPhotos([]);
      fetchReports();
    } catch (err) { console.error(err); }
    finally { setSending(false); }
  };

  const openCreate = () => {
    setForm({ description: '', date: new Date().toISOString().split('T')[0], start_time: '', end_time: '' });
    setPhotos([]);
    setSignatureStep(false);
    setDialogOpen(true);
  };

  const canSubmit = form.description && form.date && form.start_time && form.end_time;

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-orange-200 border-t-orange-500 rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="work-reports-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-gray-900">Partes de Trabajo</h1>
          <p className="text-sm text-gray-500 mt-0.5">{reports.length} partes registrados</p>
        </div>
        <Button onClick={openCreate} className="bg-[#FF6B35] hover:bg-[#E55A2B] text-white" data-testid="new-report-btn">
          <Plus className="w-4 h-4 mr-2" /> Nuevo Parte
        </Button>
      </div>

      {/* Reports list */}
      <div className="space-y-3">
        {reports.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-12 text-center text-gray-400">
            <ClipboardList className="w-10 h-10 mx-auto mb-3 text-gray-200" />
            <p>No hay partes de trabajo</p>
            <p className="text-xs mt-1">Crea tu primer parte de turno</p>
          </div>
        ) : reports.map(r => {
          const s = STATUS[r.status] || STATUS.pending;
          return (
            <div key={r.id} onClick={() => setDetailReport(r)} className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md hover:border-orange-200 transition-all cursor-pointer">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className={`${s.bg} text-xs`}>{s.label}</Badge>
                    {r.photos?.length > 0 && <Badge className="bg-blue-50 text-blue-600 text-xs"><Image className="w-3 h-3 mr-1" />{r.photos.length} fotos</Badge>}
                  </div>
                  <p className="text-sm text-gray-900 line-clamp-2">{r.description}</p>
                  <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{r.date}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{r.start_time} - {r.end_time}</span>
                  </div>
                </div>
                {r.signature_path && <PenTool className="w-4 h-4 text-green-500 flex-shrink-0 mt-1" />}
              </div>
            </div>
          );
        })}
      </div>

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuevo Parte de Trabajo</DialogTitle>
            <DialogDescription>Registra lo que sucedio durante tu turno</DialogDescription>
          </DialogHeader>

          {signatureStep ? (
            <div className="space-y-3">
              <p className="text-sm font-medium text-gray-700">Firma el parte con tu dedo o raton:</p>
              <SignaturePad onSave={handleSignatureComplete} onCancel={() => setSignatureStep(false)} title="Firma el parte" />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-gray-600">Fecha *</Label>
                  <Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} data-testid="report-date" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-gray-600">Hora inicio *</Label>
                  <Input type="time" value={form.start_time} onChange={e => setForm({...form, start_time: e.target.value})} data-testid="report-start" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-gray-600">Hora fin *</Label>
                  <Input type="time" value={form.end_time} onChange={e => setForm({...form, end_time: e.target.value})} data-testid="report-end" />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-gray-600">Descripcion del turno *</Label>
                <Textarea rows={4} value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Describe lo que sucedio durante tu turno..." className="resize-none" data-testid="report-description" />
              </div>

              {/* Photos */}
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-gray-600">Fotos (max 5)</Label>
                <input type="file" ref={photoInputRef} accept="image/*" multiple onChange={handlePhotos} className="hidden" data-testid="report-photos-input" />
                <div className="flex flex-wrap gap-2">
                  {photos.map((p, i) => (
                    <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden border border-gray-200">
                      <img src={URL.createObjectURL(p)} alt="" className="w-full h-full object-cover" />
                      <button onClick={() => setPhotos(prev => prev.filter((_,j) => j !== i))} className="absolute top-0.5 right-0.5 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                        <Trash2 className="w-2.5 h-2.5 text-white" />
                      </button>
                    </div>
                  ))}
                  {photos.length < 5 && (
                    <button onClick={() => photoInputRef.current?.click()} className="w-16 h-16 rounded-lg border-2 border-dashed border-gray-200 hover:border-orange-300 flex items-center justify-center transition-colors">
                      <Camera className="w-5 h-5 text-gray-400" />
                    </button>
                  )}
                </div>
              </div>

              <Button onClick={() => setSignatureStep(true)} disabled={!canSubmit || sending} className="w-full bg-[#FF6B35] hover:bg-[#E55A2B] text-white" data-testid="report-sign-btn">
                {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Enviando...</> : <><PenTool className="w-4 h-4 mr-2" />Firmar y Enviar</>}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!detailReport} onOpenChange={() => setDetailReport(null)}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Parte de Trabajo</DialogTitle>
            <DialogDescription>{detailReport?.date} — {detailReport?.start_time} a {detailReport?.end_time}</DialogDescription>
          </DialogHeader>
          {detailReport && (
            <div className="space-y-4">
              <Badge className={`${(STATUS[detailReport.status] || STATUS.pending).bg}`}>{(STATUS[detailReport.status] || STATUS.pending).label}</Badge>
              <p className="text-sm text-gray-900 whitespace-pre-wrap">{detailReport.description}</p>
              {detailReport.photos?.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {detailReport.photos.map((p, i) => (
                    <a key={i} href={p.url} target="_blank" rel="noopener noreferrer" className="rounded-lg overflow-hidden border border-gray-200 aspect-square block">
                      <img src={p.url} alt={p.filename} className="w-full h-full object-cover" />
                    </a>
                  ))}
                </div>
              )}
              {detailReport.signature_path && <div className="flex items-center gap-2 text-xs text-green-600"><PenTool className="w-3.5 h-3.5" />Firmado digitalmente</div>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
